import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link, Prompt } from 'react-router-dom'
import { enqueueScanProtocol, fetchScanProtocolQueue, kickScanProtocolProcessing, uploadScanProtocolFile } from '../lib/crmClient'
import { createScanDateMetadata } from '../lib/scanProtocol'

const ACTIVE_SCAN_STATUSES = new Set(['uploading', 'queued', 'pending', 'processing'])

const getFirstPresent = (source, keys) => {
  if (!source || !Array.isArray(keys)) return null

  for (const key of keys) {
    const value = source[key]
    if (value !== undefined && value !== null && value !== '') {
      return value
    }
  }

  return null
}

const getStatusLabel = (status) => {
  if (status === 'uploading') return 'Nahrávám'
  if (status === 'queued' || status === 'pending') return 'Ve frontě'
  if (status === 'processing') return 'Zpracovává se'
  if (status === 'processed') return 'Zpracováno'
  if (status === 'invalid') return 'Neplatný scan'
  if (status === 'failed') return 'Chyba'
  return status || 'Uloženo'
}

const normalizeQueueItem = (item, fallback = {}) => {
  const extracted = (item && (item.extracted || item.ExtractedJson)) || null
  const processingStatus = item && (item.processingStatus || item.ProcessingStatus || fallback.processingStatus || 'queued')

  return {
    localKey: fallback.localKey || String((item && (item.id || item.Id || item.guid || item.Guid)) || `${Date.now()}-${fallback.fileName || 'scan'}`),
    id: item && (item.id || item.Id || null),
    guid: item && (item.guid || item.Guid || null),
    fileName: (item && (item.fileName || item.SourceFileName)) || fallback.fileName || 'scan.jpg',
    statusCode: processingStatus,
    status: getStatusLabel(processingStatus),
    result: {
      ...item,
      extracted,
      processingStatus,
      processingError: item && (item.processingError || item.ProcessingError || null),
      invalidReason: item && (item.invalidReason || fallback.invalidReason || null),
    },
  }
}

export const mergeQueueItems = (current, queued) => {
  const currentById = new Map(current.filter((item) => item.id).map((item) => [String(item.id), item]))
  const returnedIds = new Set(queued.map((item) => String(item.id || item.Id)))
  const mergedQueued = queued.map((item) => normalizeQueueItem(item, currentById.get(String(item.id || item.Id)) || {}))
  const localOnly = current.filter((item) => !item.id || !returnedIds.has(String(item.id)))

  return [...localOnly, ...mergedQueued].sort((left, right) => Number(right.id || 0) - Number(left.id || 0))
}

const createCanvas = (width, height) => {
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  return canvas
}

const clamp = (value, min, max) => Math.min(Math.max(value, min), max)
const MAX_CAPTURE_SIDE = 2800

const enhanceImage = (canvas) => {
  const context = canvas.getContext('2d', { willReadFrequently: true })
  const image = context.getImageData(0, 0, canvas.width, canvas.height)
  const { data } = image

  for (let offset = 0; offset < data.length; offset += 4) {
    for (let channel = 0; channel < 3; channel += 1) {
      const value = data[offset + channel]
      const contrasted = ((value - 128) * 1.12) + 132
      data[offset + channel] = clamp(Math.round(contrasted), 0, 255)
    }
  }

  context.putImageData(image, 0, 0)
}

const prepareCapturedCanvas = (videoElement) => {
  const sourceCanvas = createCanvas(videoElement.videoWidth, videoElement.videoHeight)
  const sourceContext = sourceCanvas.getContext('2d')
  sourceContext.drawImage(videoElement, 0, 0, sourceCanvas.width, sourceCanvas.height)

  const sourceWidth = sourceCanvas.width
  const sourceHeight = sourceCanvas.height

  const longestSide = Math.max(sourceWidth, sourceHeight)
  const targetScale = longestSide > MAX_CAPTURE_SIDE ? (MAX_CAPTURE_SIDE / longestSide) : 1
  const outputWidth = Math.max(1, Math.round(sourceWidth * targetScale))
  const outputHeight = Math.max(1, Math.round(sourceHeight * targetScale))
  const outputCanvas = createCanvas(outputWidth, outputHeight)
  const outputContext = outputCanvas.getContext('2d')
  outputContext.imageSmoothingEnabled = true
  outputContext.imageSmoothingQuality = 'high'
  outputContext.drawImage(sourceCanvas, 0, 0, sourceWidth, sourceHeight, 0, 0, outputWidth, outputHeight)
  enhanceImage(outputCanvas)
  return outputCanvas
}

const createCapturedFile = async (videoElement) => {
  if (!videoElement || !videoElement.videoWidth || !videoElement.videoHeight) {
    throw new Error('Foťák ještě není připravený.')
  }

  const canvas = prepareCapturedCanvas(videoElement)

  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob((value) => {
      if (value) {
        resolve(value)
        return
      }
      reject(new Error('Nepodařilo se vytvořit snímek z foťáku.'))
    }, 'image/jpeg', 0.92)
  })

  const stamp = new Date().toISOString().replace(/[:.]/g, '-')
  return new File([blob], `${stamp}-scan.jpg`, { type: 'image/jpeg' })
}

const ScanProtokoly = ({ email }) => {
  const fileInputRef = useRef(null)
  const videoRef = useRef(null)
  const streamRef = useRef(null)
  const locationRef = useRef({ latitude: null, longitude: null })
  const processingKickRef = useRef(false)
  const pollingRef = useRef(false)
  const captureLockRef = useRef(false)
  const shutterPointerRef = useRef(null)
  const pendingFilesRef = useRef(new Map())
  const uploadLocksRef = useRef(new Set())
  const nextFileId = useRef(0)
  const [scanMode, setScanMode] = useState('same-day')
  const [importingFiles, setImportingFiles] = useState(false)
  const [error, setError] = useState('')
  const [items, setItems] = useState([])
  const [sessionActive, setSessionActive] = useState(false)
  const [showResults, setShowResults] = useState(false)
  const [cameraReady, setCameraReady] = useState(false)
  const [cameraError, setCameraError] = useState('')
  const [cameraSupported, setCameraSupported] = useState(true)
  const [uploadingCount, setUploadingCount] = useState(0)
  const [capturing, setCapturing] = useState(false)

  const sessionIdsKey = useMemo(() => items.map((item) => item.id).filter(Boolean).sort((left, right) => left - right).join(','), [items])
  const pendingCount = items.filter((item) => ACTIVE_SCAN_STATUSES.has(item.statusCode)).length
  const processedCount = items.filter((item) => item.statusCode === 'processed').length
  const invalidCount = items.filter((item) => item.statusCode === 'invalid').length
  const failedCount = items.filter((item) => item.statusCode === 'failed').length
  const hasUnsavedFiles = importingFiles || items.some((item) => !item.id)

  useEffect(() => {
    if (!hasUnsavedFiles) return undefined
    const warn = (event) => { event.preventDefault(); event.returnValue = '' }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [hasUnsavedFiles])

  useEffect(() => () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop())
      streamRef.current = null
    }
  }, [])

  useEffect(() => {
    if (!sessionActive) return undefined
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [sessionActive])

  useEffect(() => {
    const sessionIds = sessionIdsKey.split(',').map(Number).filter(Boolean)
    const shouldPoll = sessionIds.length > 0 && pendingCount > 0
    if (!shouldPoll) return undefined

    let active = true

    const syncQueue = async () => {
      if (pollingRef.current) return
      pollingRef.current = true
      try {
        if (pendingCount > 0 && !processingKickRef.current) {
          processingKickRef.current = true
          kickScanProtocolProcessing({ limit: 1, ids: sessionIds }).catch(() => {}).finally(() => {
            processingKickRef.current = false
          })
        }

        const queueItems = await fetchScanProtocolQueue({ limit: sessionIds.length || 50, ids: sessionIds })
        if (!active) return
        setItems((prev) => mergeQueueItems(prev, queueItems))
      } catch {
        if (!active) return
      } finally {
        pollingRef.current = false
      }
    }

    syncQueue()
    const timer = window.setInterval(syncQueue, 5000)

    return () => {
      active = false
      window.clearInterval(timer)
    }
  }, [pendingCount, sessionActive, sessionIdsKey, showResults])

  const openPicker = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  const getLocation = async () => {
    if (!navigator.geolocation) return { latitude: null, longitude: null }
    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (position) => resolve({ latitude: position.coords.latitude, longitude: position.coords.longitude }),
        () => resolve({ latitude: null, longitude: null }),
        { enableHighAccuracy: true, timeout: 6000, maximumAge: 120000 }
      )
    })
  }

  const ensureLocation = async () => {
    if (locationRef.current.latitude !== null || locationRef.current.longitude !== null) {
      return locationRef.current
    }
    const location = await getLocation()
    locationRef.current = location
    return location
  }

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop())
      streamRef.current = null
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null
    }
    setCameraReady(false)
  }

  const startCamera = async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setCameraSupported(false)
      setCameraError('Prohlížeč nepodporuje přímý foťák v aplikaci.')
      return
    }

    setCameraError('')
    setCameraSupported(true)
    stopCamera()

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 3840 },
          height: { ideal: 2160 },
        },
        audio: false,
      })

      streamRef.current = stream
      const videoElement = videoRef.current
      if (!videoElement) {
        throw new Error('Náhled foťáku ještě není připravený.')
      }
      videoElement.srcObject = stream
      if (videoElement.readyState < 1) {
        await new Promise((resolve) => {
          videoElement.addEventListener('loadedmetadata', resolve, { once: true })
        })
      }
      await videoElement.play()
      if (!videoElement.videoWidth || !videoElement.videoHeight) {
        throw new Error('Foťák neposkytl platný obraz.')
      }
      setCameraReady(true)
    } catch (cameraStartError) {
      stopCamera()
      setCameraError(cameraStartError instanceof Error ? cameraStartError.message : 'Nepodařilo se spustit foťák.')
    }
  }

  const beginSession = async () => {
    setError('')
    setCameraError('')
    setShowResults(false)
    setSessionActive(true)
    await new Promise((resolve) => window.requestAnimationFrame(() => resolve()))
    locationRef.current = { latitude: null, longitude: null }
    if (scanMode === 'same-day') ensureLocation().catch(() => {})
    await startCamera()
  }

  const finalizeSession = () => {
    setSessionActive(false)
    setShowResults(true)
    stopCamera()
  }

  const resetSession = () => {
    if (hasUnsavedFiles) return
    setItems([])
    setError('')
    setCameraError('')
    setSessionActive(false)
    setShowResults(false)
    stopCamera()
  }

  const enqueueFile = async (file, retryId, metadata = createScanDateMetadata(scanMode)) => {
    const localId = retryId || `${Date.now()}-${nextFileId.current++}`
    if (uploadLocksRef.current.has(localId)) return
    uploadLocksRef.current.add(localId)
    if (!retryId) {
      pendingFilesRef.current.set(localId, { file, metadata, upload: null })
      setItems((prev) => [{ localKey: localId, id: null, fileName: file.name, statusCode: 'uploading', status: getStatusLabel('uploading'), result: null }, ...prev])
    } else {
      setItems((prev) => prev.map((item) => item.localKey === localId ? { ...item, statusCode: 'uploading', status: getStatusLabel('uploading') } : item))
    }
    setUploadingCount((prev) => prev + 1)

    try {
      const pending = pendingFilesRef.current.get(localId)
      const location = pending.metadata.scanMode === 'same-day' ? await ensureLocation() : { latitude: null, longitude: null }
      const upload = pending.upload || await uploadScanProtocolFile({ file: pending.file })
      pending.upload = upload
      const queued = await enqueueScanProtocol({
        blobPath: upload && upload.blobPath,
        blobUrl: upload && upload.blobUrl,
        ...pending.metadata,
        latitude: location.latitude,
        longitude: location.longitude,
        guid: upload && upload.guid,
      })

      if (!queued || !(queued.id || queued.Id)) throw new Error('Server nepotvrdil uložení snímku.')
      if ((queued.scanMode || queued.ScanMode) !== pending.metadata.scanMode) throw new Error('Backend zatím nepodporuje zvolený režim. Je nutná aktualizace serveru.')

      setItems((prev) => prev.map((item) => (
        item.localKey === localId
            ? normalizeQueueItem(queued, { localKey: localId, fileName: pending.file.name })
          : item
      )))
          pendingFilesRef.current.delete(localId)
    } catch (scanError) {
      const message = scanError instanceof Error ? scanError.message : 'Scan selhal.'
      setItems((prev) => prev.map((item) => (
        item.localKey === localId
          ? { ...item, statusCode: 'failed', status: getStatusLabel('failed'), result: { message } }
          : item
      )))
      setError(message)
    } finally {
      uploadLocksRef.current.delete(localId)
      setUploadingCount((prev) => Math.max(prev - 1, 0))
    }
  }

  const captureCurrentFrame = async () => {
    if (!videoRef.current || captureLockRef.current) return

    captureLockRef.current = true
    setCapturing(true)
    setError('')
    try {
      const metadata = createScanDateMetadata(scanMode)
      await new Promise((resolve) => window.requestAnimationFrame(() => resolve()))
      const file = await createCapturedFile(videoRef.current)
      enqueueFile(file, null, metadata)
    } catch (captureError) {
      setError(captureError instanceof Error ? captureError.message : 'Nepodařilo se pořídit snímek.')
    } finally {
      captureLockRef.current = false
      setCapturing(false)
    }
  }

  const handleShutterPointerDown = (event) => {
    if (!cameraReady || captureLockRef.current || !event.isPrimary) return
    shutterPointerRef.current = event.pointerId
  }

  const handleShutterPointerUp = (event) => {
    if (shutterPointerRef.current !== event.pointerId) return
    shutterPointerRef.current = null
    captureCurrentFrame()
  }

  const cancelShutterPointer = () => {
    shutterPointerRef.current = null
  }

  const handleShutterKeyDown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return
    event.preventDefault()
    captureCurrentFrame()
  }

  const handleFiles = async (event) => {
    const selectedFiles = Array.from(event.target.files || []).filter((file) => file instanceof File)
    event.target.value = ''

    if (selectedFiles.length === 0 || importingFiles) return

    setError('')
    setShowResults(true)
    setImportingFiles(true)
    const metadata = createScanDateMetadata(scanMode)
    try {
      for (const file of selectedFiles) await enqueueFile(file, null, metadata)
    } finally { setImportingFiles(false) }
  }

  const visibleItems = showResults ? items : []

  return (
    <div>
      <Prompt when={hasUnsavedFiles} message="Některé snímky nejsou uložené na serveru. Opravdu odejít?" />
      <h2 className="page-header">Scan protokoly</h2>

      <div className="card crm-scan-session-card">
        <div className="card__header">
          <h3>Sběr protokolů</h3>
        </div>

        <div className="card__body">
          <div className="crm-scan-session-head">
            <fieldset className="crm-scan-modes" disabled={sessionActive || uploadingCount > 0 || importingFiles}>
              <legend>Datum realizace</legend>
              <label><input type="radio" name="scan-mode" checked={scanMode === 'same-day'} onChange={() => setScanMode('same-day')} /> Dnešní zakázky</label>
              <label><input type="radio" name="scan-mode" checked={scanMode === 'historical'} onChange={() => setScanMode('historical')} /> Starší protokoly</label>
            </fieldset>
            <strong>{email || '-'}</strong>
          </div>

          <div className="crm-actions-row crm-actions-row--nav crm-actions-row--scan-admin-link">
            <Link className="btn" to="/scan-protokoly/sprava">Správa protokolů</Link>
          </div>

          <div className="crm-scan-kpis">
            <div className="crm-scan-kpi">
              <span>Naskenováno</span>
              <strong>{items.length}</strong>
            </div>
            <div className="crm-scan-kpi">
              <span>Rozpracováno</span>
              <strong>{pendingCount}</strong>
            </div>
            <div className="crm-scan-kpi">
              <span>Hotovo</span>
              <strong>{processedCount}</strong>
            </div>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleFiles}
            style={{ display: 'none' }}
          />

          {!sessionActive ? (
            <div className="crm-actions-row crm-actions-row--nav crm-actions-row--scan-entry">
              <button type="button" className="btn btn-primary" onClick={beginSession}>
                Začít skenovat
              </button>
              <button type="button" className="btn" onClick={openPicker} disabled={importingFiles}>
                Vybrat fotografie
              </button>
              <Link className="btn" to="/">Zpět na dashboard</Link>
            </div>
          ) : null}

          {sessionActive ? (
            <div className="crm-scan-camera-modal" role="dialog" aria-modal="true" aria-label="Skenování protokolu">
              <div className="crm-scan-camera-modal__topbar">
                <div>
                  <strong>Scan protokolu</strong>
                  <span>{scanMode === 'same-day' ? 'Dnešní zakázky' : 'Starší protokoly'}</span>
                </div>
                <div className="crm-scan-camera-modal__counter">
                  <span>Vyfoceno</span>
                  <strong>{items.length}</strong>
                </div>
              </div>

              <div className="crm-scan-camera-shell">
                <video ref={videoRef} className="crm-scan-camera" playsInline muted autoPlay />
                <div className="crm-scan-camera__guide" aria-hidden="true">
                  <div className="crm-scan-camera__guide-frame">
                    <span className="crm-scan-camera__guide-label">Umísti protokol do rámu</span>
                  </div>
                </div>
                {!cameraReady ? <div className="crm-scan-camera__overlay">{cameraError || 'Spouštím foťák…'}</div> : null}
                {capturing ? <div className="crm-scan-camera__flash" aria-hidden="true" /> : null}
              </div>

              <div className="crm-scan-camera-modal__controls">
                <button type="button" className="crm-scan-camera-modal__close" onClick={finalizeSession}>
                  <i className="bx bx-x" aria-hidden="true" />
                  <span>Ukončit</span>
                </button>
                <button
                  type="button"
                  className={`crm-scan-shutter${capturing ? ' is-capturing' : ''}`}
                  onPointerDown={handleShutterPointerDown}
                  onPointerUp={handleShutterPointerUp}
                  onPointerCancel={cancelShutterPointer}
                  onPointerLeave={cancelShutterPointer}
                  onKeyDown={handleShutterKeyDown}
                  disabled={!cameraReady || capturing}
                  aria-label={capturing ? 'Zpracovávám snímek' : 'Vyfotit protokol'}
                >
                  <span />
                </button>
                <div className="crm-scan-camera-modal__status">
                  <strong>{capturing ? 'Ukládám…' : cameraReady ? 'Připraveno' : 'Čekám na kameru'}</strong>
                  <span>{uploadingCount > 0 ? `Odesílám: ${uploadingCount}` : 'Telefon nečeká na AI'}</span>
                </div>
              </div>
            </div>
          ) : null}

          {!cameraSupported || cameraError ? (
            <p className="crm-form-note">{cameraError || 'Přímý foťák v aplikaci není dostupný, použij nouzový výběr.'}</p>
          ) : null}

          {error ? <p className="crm-error">{error}</p> : null}

          {showResults ? (
            <div className="crm-scan-session-summary">
              <div className="crm-scan-session-summary__stats">
                <span>Zpracováno: {processedCount}</span>
                <span>Neplatné: {invalidCount}</span>
                <span>Chyby: {failedCount}</span>
                <span>Ještě běží: {pendingCount}</span>
              </div>
              <div className="crm-actions-row crm-actions-row--nav crm-actions-row--scan-entry">
                <button type="button" className="btn btn-primary" onClick={beginSession}>
                  Pokračovat ve focení
                </button>
                <button type="button" className="btn" onClick={resetSession} disabled={hasUnsavedFiles}>
                  Vyčistit přehled
                </button>
              </div>
            </div>
          ) : null}

          {showResults ? (
            <div className="crm-scan-results">
              {visibleItems.length === 0 ? (
                <p className="crm-form-note">Po ukončení session se tady ukážou výsledky.</p>
              ) : visibleItems.map((item) => {
                const extracted = item.result && item.result.extracted
                const detectedName = getFirstPresent(extracted, ['Odběratel', 'Odberatel'])
                const detectedProtocol = getFirstPresent(extracted, ['Číslo protokolu', 'CisloProtokolu'])
                const detectedAmount = getFirstPresent(extracted, ['Cena zakázky', 'CenaZakazky'])
                const detectedDate = getFirstPresent(extracted, ['Datum protokolu', 'DatumProtokolu', 'Datum'])
                const itemError = item.result && (item.result.invalidReason || item.result.processingError || item.result.message || (extracted && extracted.InvalidReason))

                return (
                  <div key={item.localKey || item.id} className="crm-scan-result-card">
                    <div className="crm-scan-result-card__header">
                      <strong>{item.fileName}</strong>
                      <span>{item.status}</span>
                    </div>
                    <div className="crm-scan-result-card__grid">
                      <span>Zákazník: {detectedName || '-'}</span>
                      <span>Číslo: {detectedProtocol || '-'}</span>
                      <span>Datum: {detectedDate || '-'}</span>
                      <span>Cena: {detectedAmount ?? '-'}</span>
                    </div>
                    {itemError ? <p className="crm-form-note">{itemError}</p> : null}
                    {!item.id && item.statusCode === 'failed' ? <button type="button" className="btn" onClick={() => enqueueFile(null, item.localKey)}><i className="bx bx-refresh" aria-hidden="true" /> Znovu odeslat</button> : null}
                  </div>
                )
              })}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}

export default ScanProtokoly